interface Settings {
  noiseCancellation: boolean;
  autoSave: boolean;
  speakerDetection: boolean;
  punctuation: boolean;
  smartFormatting: boolean;
}

interface SettingsPanelProps {
  settings: Settings;
  onChange: (settings: Settings) => void;
}

const SettingsPanel = ({ settings, onChange }: SettingsPanelProps) => {
  const toggleSetting = (key: keyof Settings) => {
    onChange({
      ...settings,
      [key]: !settings[key]
    });
  };

  const settingsConfig = [
    {
      key: 'noiseCancellation' as keyof Settings,
      icon: 'ri-volume-mute-fill',
      title: 'Noise Cancellation',
      description: 'Filter out background noise for clearer recordings',
      color: 'teal'
    },
    {
      key: 'autoSave' as keyof Settings,
      icon: 'ri-save-fill',
      title: 'Auto-Save',
      description: 'Automatically save your notes and transcripts',
      color: 'blue'
    },
    {
      key: 'speakerDetection' as keyof Settings,
      icon: 'ri-user-voice-fill',
      title: 'Speaker Detection',
      description: 'Identify and label different speakers in the transcript',
      color: 'purple'
    },
    {
      key: 'punctuation' as keyof Settings,
      icon: 'ri-text',
      title: 'Auto Punctuation',
      description: 'Automatically add punctuation to transcribed text',
      color: 'orange'
    },
    {
      key: 'smartFormatting' as keyof Settings,
      icon: 'ri-magic-fill',
      title: 'Smart Formatting',
      description: 'Apply intelligent formatting to improve readability',
      color: 'pink'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-slate-500 to-slate-700 rounded-lg flex items-center justify-center">
          <i className="ri-settings-3-fill text-white text-sm"></i>
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Recording Settings</h3>
      </div>

      <div className="space-y-3">
        {settingsConfig.map((setting) => (
          <div
            key={setting.key}
            className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
          >
            <div className="flex items-start gap-3 flex-1">
              <div className={`w-10 h-10 bg-${setting.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${setting.icon} text-${setting.color}-600`}></i>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-slate-900 mb-1">{setting.title}</h4>
                <p className="text-xs text-slate-500 leading-relaxed">{setting.description}</p>
              </div>
            </div>
            <button
              onClick={() => toggleSetting(setting.key)}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${
                settings[setting.key] ? 'bg-teal-500' : 'bg-slate-300'
              }`}
            >
              <div
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  settings[setting.key] ? 'translate-x-7' : 'translate-x-1'
                }`}
              ></div>
            </button>
          </div>
        ))}
      </div>

      <div className="pt-6 border-t border-slate-200">
        <h4 className="text-sm font-semibold text-slate-900 mb-3">Export Format</h4>
        <div className="grid grid-cols-2 gap-3">
          <button className="px-4 py-3 bg-white border-2 border-teal-500 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-file-text-line"></i>
            TXT
          </button>
          <button className="px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-file-pdf-line"></i>
            PDF
          </button>
          <button className="px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-file-word-line"></i>
            DOCX
          </button>
          <button className="px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-markdown-line"></i>
            MD
          </button>
        </div>
      </div>

      <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <i className="ri-information-fill text-slate-600 text-lg flex-shrink-0"></i>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">About Settings</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              These settings apply to all new recordings. Changes won't affect existing transcripts. For best results, enable noise cancellation in noisy environments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;